import type { CSVValidationError, ParsedCSVRow, CSVParseResult } from '../types/asset-import.types';

const REQUIRED_COLUMNS = [
  'Asset Code',
  'Description',
  'Category',
  'Bought Value',
  'Bought Date'
];

const OPTIONAL_COLUMNS = [
  'Supplier',
  'Serial Number',
  'Location',
  'Notes'
];

export const parseCSV = (file: File): Promise<CSVParseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          resolve({ data: [], errors: [{ row: 0, field: 'File', error: 'File is empty' }] });
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        const errors: CSVValidationError[] = [];

        // Validate required columns
        REQUIRED_COLUMNS.forEach((column) => {
          if (!headers.includes(column)) {
            errors.push({
              row: 0,
              field: column,
              error: `Missing required column: ${column}`
            });
          }
        });

        if (errors.length > 0) {
          resolve({ data: [], errors });
          return;
        }

        // Parse data rows
        const data: ParsedCSVRow[] = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const row: ParsedCSVRow = {};
          
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });

          data.push(row);
        }

        resolve({ data, errors: [] });
      } catch (error) {
        reject(new Error(`CSV parsing failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
};

export const generateCSVTemplate = (): string => {
  const headers = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];
  const exampleRows = [
    {
      'Asset Code': 'FA001',
      'Description': 'Dell XPS 15 Laptop',
      'Category': 'IT Equipment',
      'Bought Value': '1200.00',
      'Bought Date': '10/01/2025',
      'Supplier': 'Dell Technologies',
      'Serial Number': 'XPS15-78432',
      'Location': 'Head Office',
      'Notes': 'Assigned to John Smith'
    },
    {
      'Asset Code': 'FA002',
      'Description': 'Herman Miller Chair',
      'Category': 'Furniture',
      'Bought Value': '890.00',
      'Bought Date': '08/01/2025',
      'Supplier': 'Office Supplies Ltd',
      'Serial Number': 'HM-2025-001',
      'Location': 'Head Office',
      'Notes': ''
    },
    {
      'Asset Code': 'FA003',
      'Description': 'Ford Transit Custom',
      'Category': 'Vehicles',
      'Bought Value': '28500.00',
      'Bought Date': '05/01/2025',
      'Supplier': 'Ford Dealer UK',
      'Serial Number': 'WF0XXXGCD',
      'Location': 'Fleet',
      'Notes': 'Company van'
    }
  ];

  // Create CSV string
  let csv = headers.join(',') + '\n';
  exampleRows.forEach(row => {
    const values = headers.map(header => {
      const value = row[header as keyof typeof row] || '';
      // Escape values with commas
      return value.includes(',') ? `"${value}"` : value;
    });
    csv += values.join(',') + '\n';
  });

  return csv;
};

export const downloadCSVTemplate = () => {
  const csv = generateCSVTemplate();
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'asset-import-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
